<template>
  <div class="resignation-management">
    <div class="main-content">
      <div class="filter-section">
        <div class="filter-form">
          <div class="filter-item">
            <a-input
              v-model:value="filters.searchText"
              placeholder="搜索姓名 / 身份证号..."
              allowClear
              @pressEnter="handleSearch"
              @change="handleSearchChange"
            />
          </div>
          <div class="filter-item">
            <a-select
              v-model:value="filters.status"
              placeholder="全部状态"
              allowClear
              style="width: 100%;"
              @change="handleFilterChange"
            >
              <a-select-option value="">全部状态</a-select-option>
              <a-select-option value="pending">待发起</a-select-option>
              <a-select-option value="signing">员工签署中</a-select-option>
              <a-select-option value="signed">签署完成</a-select-option>
              <a-select-option value="archived">已归档</a-select-option>
              <a-select-option value="expired">已过期</a-select-option>
              <a-select-option value="rejected">员工拒绝签署</a-select-option>
            </a-select>
          </div>
          <div class="filter-item">
            <a-select
              v-model:value="filters.customer"
              placeholder="全部客户"
              allowClear
              style="width: 100%;"
              @change="handleFilterChange"
            >
              <a-select-option value="">全部客户</a-select-option>
              <a-select-option v-for="c in customerOptions" :key="c" :value="c">{{ c }}</a-select-option>
            </a-select>
          </div>
          <div class="filter-item">
            <a-select
              v-model:value="filters.workCenter"
              placeholder="全部工作中心"
              allowClear
              style="width: 100%;"
              @change="handleFilterChange"
            >
              <a-select-option value="">全部工作中心</a-select-option>
              <a-select-option v-for="w in workCenterOptions" :key="w" :value="w">{{ w }}</a-select-option>
            </a-select>
          </div>
        </div>
        <div class="filter-actions">
          <a-button type="primary" @click="handleSearch" style="border-radius: 4px;">查询</a-button>
          <a-button @click="handleReset" style="border-radius: 4px;">重置</a-button>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <div class="table-header-left">
            <div class="stat-item">
              <span class="stat-label">离职申请总数</span>
              <span class="stat-value primary">{{ statistics.total }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">员工签署中</span>
              <span class="stat-value warning">{{ statistics.signing }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">已归档</span>
              <span class="stat-value success">{{ statistics.archived }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">异常</span>
              <span class="stat-value danger">{{ statistics.exception }}</span>
            </div>
          </div>
          <div class="table-header-right">
            <a-button @click="handleExport" style="border-radius: 4px; margin-right: 8px;">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style="margin-right: 4px;">
                <path d="M7 0v8M3 5l4 4 4-4M0 10v2a2 2 0 002 2h10a2 2 0 002-2v-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              导出
            </a-button>
          </div>
        </div>
        <vxe-table
          ref="vxeTableRef"
          :data="filteredData"
          auto-resize
          :column-config="{ resizable: true }"
          :row-config="{ isHover: true, height: 40 }"
          :show-overflow="'title'"
        >
          <vxe-column type="seq" width="60" title="序号" fixed="left" />
          <vxe-column field="name" title="姓名" min-width="100" fixed="left" />
          <vxe-column field="phone" title="手机号" min-width="120" />
          <vxe-column field="idCard" title="身份证号" min-width="180">
            <template #default="{ row }">
              <span>{{ row.idCard }}</span>
            </template>
          </vxe-column>
          <vxe-column field="customer" title="客户名称" min-width="120" />
          <vxe-column field="workCenter" title="工作中心" min-width="140" />
          <vxe-column field="contractStartDate" title="合同开始日期" min-width="130" />
          <vxe-column field="status" title="合同状态" min-width="120">
            <template #default="{ row }">
              <span v-if="row.status === 'pending'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="warning" />待发起
              </span>
              <span v-else-if="row.status === 'signing'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="processing" />员工签署中
              </span>
              <span v-else-if="row.status === 'signed'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="success" />签署完成
              </span>
              <span v-else-if="row.status === 'archived'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="success" />已归档
              </span>
              <span v-else-if="row.status === 'expired'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="error" />已过期
              </span>
              <span v-else-if="row.status === 'rejected'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="error" />员工拒绝签署
              </span>
            </template>
          </vxe-column>
          <vxe-column field="contractBody" title="合同主体" min-width="180" />
          <vxe-column title="操作" width="120" fixed="right" align="right">
            <template #default="{ row }">
              <template v-if="row.status === 'pending'">
                <button class="link-button" @click="handleResign(row)">重新发起</button>
                <button class="link-button" @click="handleView(row)">查看</button>
              </template>
              <template v-else-if="row.status === 'signing'">
                <button class="link-button" @click="handleRemind(row)">催签</button>
                <button class="link-button" @click="handleView(row)">查看</button>
              </template>
              <template v-else-if="row.status === 'expired' || row.status === 'rejected'">
                <button class="link-button" @click="handleResign(row)">重签</button>
                <button class="link-button" @click="handleView(row)">查看</button>
              </template>
              <template v-else>
                <button class="link-button" @click="handleView(row)">查看</button>
              </template>
            </template>
          </vxe-column>
        </vxe-table>

        <div class="pagination-wrapper">
          <a-pagination
            v-model:current="currentPage"
            :page-size="pageSize"
            :page-size-options="['10', '20', '50', '100']"
            :total="filteredData.length"
            :showSizeChanger="true"
            :showQuickJumper="true"
            :showTotal="showTotalText"
            @change="handlePageChange"
            @showSizeChange="handleSizeChange"
          />
        </div>
      </div>
    </div>

    <Drawer :visible="viewModalVisible" title="离职详情" @update:visible="viewModalVisible = $event">
      <div v-if="viewData" class="form-wrapper">
        <div class="form-title">员工基本信息</div>
        <div class="form-card">
          <div class="form-grid">
            <div class="detail-item">
              <span class="detail-label">姓名</span>
              <span class="detail-value">{{ viewData.name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">手机号</span>
              <span class="detail-value">{{ viewData.phone || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">身份证号</span>
              <span class="detail-value">{{ viewData.idCard }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">客户名称</span>
              <span class="detail-value">{{ viewData.customer }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">工作中心</span>
              <span class="detail-value">{{ viewData.workCenter }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">合同开始日期</span>
              <span class="detail-value">{{ viewData.contractStartDate }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">合同主体</span>
              <span class="detail-value">{{ viewData.contractBody || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">合同状态</span>
              <span class="detail-value">
                <span v-if="viewData.status === 'pending'" style="display: flex; align-items: center; gap: 4px;">
                  <a-badge status="warning" />待发起
                </span>
                <span v-else-if="viewData.status === 'signing'" style="display: flex; align-items: center; gap: 4px;">
                  <a-badge status="processing" />员工签署中
                </span>
                <span v-else-if="viewData.status === 'signed'" style="display: flex; align-items: center; gap: 4px;">
                  <a-badge status="success" />签署完成
                </span>
                <span v-else-if="viewData.status === 'archived'" style="display: flex; align-items: center; gap: 4px;">
                  <a-badge status="success" />已归档
                </span>
                <span v-else-if="viewData.status === 'expired'" style="display: flex; align-items: center; gap: 4px;">
                  <a-badge status="error" />已过期
                </span>
                <span v-else-if="viewData.status === 'rejected'" style="display: flex; align-items: center; gap: 4px;">
                  <a-badge status="error" />员工拒绝签署
                </span>
              </span>
            </div>
          </div>
        </div>

        <div class="form-title" style="margin-top: 24px;">合同签署流程</div>
        <div class="form-card">
          <div class="timeline-list">
            <div v-for="(item, index) in [...(viewData.timeline || [])].reverse()" :key="index" class="timeline-item">
              <div class="timeline-dot" :style="{ backgroundColor: getTimelineColor(item.color) }"></div>
              <div class="timeline-content">
                <div class="timeline-title">{{ item.title }}</div>
                <div class="timeline-time">{{ item.time }}</div>
                <div class="timeline-desc">{{ item.desc }}</div>
              </div>
            </div>
            <div v-if="viewData.status === 'pending'" class="timeline-item">
              <div class="timeline-dot" style="background-color: #516EFD;"></div>
              <div class="timeline-content">
                <div class="timeline-title">生成员工离职申请单</div>
                <div class="timeline-time">{{ viewData.createTime }}</div>
                <div class="timeline-desc">外包公司系统生成离职申请单</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>

    <a-modal
      v-model:visible="contractModalVisible"
      title="发起解除劳动合同协议"
      @ok="handleContractSubmit"
      :okText="contractSubmitLoading ? '正在发起中...' : '确认发起'"
      cancelText="取消"
      width="600px"
    >
      <a-form :form="contractForm" layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="员工姓名">
              <a-input v-decorator="['name']" readOnly style="background: #F7F8FA;" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="身份证号">
              <a-input v-decorator="['idCard']" readOnly style="background: #F7F8FA;" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="客户名称">
              <a-input v-decorator="['customer']" readOnly style="background: #F7F8FA;" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="工作中心">
              <a-input v-decorator="['workCenter']" readOnly style="background: #F7F8FA;" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="合同主体">
              <a-select
                v-decorator="['contractBody', { initialValue: '' }]"
                placeholder="请选择合同主体"
              >
                <a-select-option value="深圳外包服务有限公司">深圳外包服务有限公司</a-select-option>
                <a-select-option value="北京人力资源服务公司">北京人力资源服务公司</a-select-option>
                <a-select-option value="上海人才服务有限公司">上海人才服务有限公司</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="合同开始日期" required>
              <a-date-picker
                v-decorator="['contractStartDate', { rules: [{ required: true, message: '请选择合同开始日期' }] }]"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item label="合同模板" required>
          <a-select
            v-decorator="['contractTemplate', { rules: [{ required: true, message: '请选择合同模板' }] }]"
            placeholder="请选择合同模板"
          >
            <a-select-option value="standard">解除劳动合同协议-标准版</a-select-option>
            <a-select-option value="negotiate">解除劳动合同协议-协商版</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea v-decorator="['remark']" :rows="3" placeholder="可选填写备注信息..." />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import resignationDataSource from '@/data/resignationSource'
import Drawer from '../components/Drawer.vue'

export default {
  name: 'ResignationManagement',
  components: {
    Drawer
  },
  data() {
    return {
      filters: {
        searchText: '',
        status: undefined,
        customer: undefined,
        workCenter: undefined
      },
      currentPage: 1,
      pageSize: 10,
      currentRecordId: null,
      viewModalVisible: false,
      contractModalVisible: false,
      contractSubmitLoading: false,
      viewData: null,
      customerOptions: ['腾讯科技', '阿里巴巴', '字节跳动', '美团'],
      workCenterOptions: ['深圳研发中心', '北京运营中心', '上海商务中心'],
      contractForm: this.$form.createForm(this),
      tableData: []
    }
  },
  computed: {
    filteredData() {
      let data = [...this.tableData]
      
      if (this.filters.searchText) {
        const search = this.filters.searchText.toLowerCase()
        data = data.filter(item => 
          item.name.toLowerCase().includes(search) || 
          item.idCard.includes(search)
        )
      }
      
      if (this.filters.status) {
        data = data.filter(item => item.status === this.filters.status)
      }
      
      if (this.filters.customer) {
        data = data.filter(item => item.customer === this.filters.customer)
      }
      
      if (this.filters.workCenter) {
        data = data.filter(item => item.workCenter === this.filters.workCenter)
      }
      
      return data.sort((a, b) => {
        const timeA = a.createTime || '1970-01-01 00:00:00'
        const timeB = b.createTime || '1970-01-01 00:00:00'
        return timeB.localeCompare(timeA)
      })
    },
    statistics() {
      const total = this.tableData.length
      const signing = this.tableData.filter(item => item.status === 'signing').length
      const archived = this.tableData.filter(item => item.status === 'archived' || item.status === 'signed').length
      const exception = this.tableData.filter(item => item.status === 'expired' || item.status === 'rejected').length
      return { total, signing, archived, exception }
    }
  },
  mounted() {
    resignationDataSource.init((data) => {
      this.tableData = data
    })

    this.receiveDataFromThirdParty()
  },
  methods: {
    formatIdCard(idCard) {
      if (!idCard) return '-'
      return idCard
    },
    getTimelineColor(color) {
      const colorMap = {
        'blue': '#516EFD',
        'green': '#0DD78D',
        'orange': '#FEAE4D',
        'red': '#FD5451'
      }
      return colorMap[color] || '#516EFD'
    },
    getStatusText(status) {
      const statusMap = {
        'pending': '待发起',
        'signing': '员工签署中',
        'signed': '签署完成',
        'archived': '已归档',
        'expired': '已过期',
        'rejected': '员工拒绝签署'
      }
      return statusMap[status] || status
    },
    showTotalText(total) {
      return `共 ${total} 条`
    },
    handleSearch() {
      this.currentPage = 1
    },
    handleSearchChange() {
      this.handleSearch()
    },
    handleFilterChange() {
      this.currentPage = 1
    },
    handleReset() {
      this.filters = {
        searchText: '',
        status: undefined,
        customer: undefined,
        workCenter: undefined
      }
      this.currentPage = 1
    },
    handleSizeChange(current, size) {
      this.pageSize = size
      this.currentPage = 1
    },
    handlePageChange(page) {
      this.currentPage = page
    },
    handleExport() {
      this.$message.success('导出功能开发中')
    },
    handleCreate() {
      this.contractForm.resetFields()
      this.contractModalVisible = true
    },
    handleView(record) {
      this.viewData = record
      this.viewModalVisible = true
    },
    handleContract(record) {
      this.currentRecordId = record.id
      this.contractModalVisible = true
      setTimeout(() => {
        this.contractForm.setFieldsValue({
          name: record.name,
          idCard: record.idCard,
          customer: record.customer,
          workCenter: record.workCenter,
          contractBody: record.contractBody,
          contractStartDate: record.contractStartDate ? moment(record.contractStartDate, 'YYYY-MM-DD') : null,
          contractTemplate: record.contractTemplate,
          remark: record.remark
        })
      }, 100)
    },
    handleContractSubmit() {
      this.contractSubmitLoading = true
      this.contractForm.validateFields((err, values) => {
        if (!err) {
          setTimeout(() => {
            const recordId = this.viewData?.id || this.currentRecordId
            if (recordId) {
              const record = resignationDataSource.getItem(recordId)
              if (record) {
                resignationDataSource.updateItem(recordId, {
                  contractBody: values.contractBody,
                  contractStartDate: values.contractStartDate ? values.contractStartDate.format('YYYY-MM-DD') : values.contractStartDate,
                  contractTemplate: values.contractTemplate,
                  remark: values.remark
                })
                resignationDataSource.updateStatus(recordId, 'signing', {
                  title: '发起解除劳动合同协议',
                  desc: `合同主体：${values.contractBody || record.contractBody || '-'}，合同模板：${values.contractTemplate || '-'}`,
                  color: 'blue'
                })
              }
            }
            this.$message.success('合同发起成功')
            this.pushToThirdParty()
            this.contractModalVisible = false
            this.contractForm.resetFields()
            this.contractSubmitLoading = false
          }, 1000)
        } else {
          this.contractSubmitLoading = false
        }
      })
    },
    handleRemind(record) {
      this.$confirm({
        title: '确认催签',
        content: `确定要向 ${record.name} 发送催签提醒吗？`,
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          resignationDataSource.updateStatus(record.id, record.status, {
            title: '合同催签',
            desc: '系统自动发送催签短信提醒',
            color: 'orange'
          })
          this.$message.success('已发送催签提醒')
          this.pushToThirdParty()
        }
      })
    },
    handleResign(record) {
      const isPending = record.status === 'pending'
      const title = isPending ? '确认重新发起' : '确认重签'
      const content = isPending
        ? `确定要为员工 ${record.name} 重新发起合同签署吗？`
        : `确定要为员工 ${record.name} 重新发起合同签署吗？`

      this.$confirm({
        title,
        content,
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          resignationDataSource.updateStatus(record.id, 'signing', {
            title: isPending ? '重新发起合同' : '合同重签',
            desc: isPending ? '重新发起合同签署流程' : '重新发起合同签署流程',
            color: 'blue'
          })
          this.$message.success(isPending ? '重新发起成功' : '重签成功')
          this.pushToThirdParty()
        }
      })
    },
    receiveDataFromThirdParty() {
      const thirdPartyData = [
        {
          id: 1,
          name: '张三',
          idCard: '320123199001011234',
          phone: '13800138001',
          customer: '腾讯科技',
          workCenter: '深圳研发中心',
          contractStartDate: '2026-05-15',
          contractBody: '深圳外包服务有限公司',
          contractTemplate: 'standard',
          remark: '员工主动申请离职，已完成工作交接',
          status: 'archived',
          createTime: '2026-05-15 09:00:00',
          timeline: [
            { title: '合同自动归档', time: '2026-05-16 14:32:10', desc: '员工签署完成，系统自动归档合同', color: 'green' },
            { title: '员工签署完成', time: '2026-05-16 10:15:30', desc: '张三通过电子签章完成合同签署', color: 'green' },
            { title: '合同催签', time: '2026-05-15 18:00:00', desc: '系统自动发送催签短信提醒', color: 'orange' },
            { title: '发起解除劳动合同协议', time: '2026-05-15 09:30:00', desc: 'HR李经理发起解除劳动合同协议', color: 'blue' }
          ]
        },
        {
          id: 2,
          name: '李四',
          idCard: '440123199002022345',
          phone: '13800138002',
          customer: '阿里巴巴',
          workCenter: '北京运营中心',
          contractStartDate: '2026-05-18',
          contractBody: '北京人力资源服务公司',
          contractTemplate: 'standard',
          remark: '试用期员工离职原因个人发展',
          status: 'signing',
          createTime: '2026-05-18 09:00:00',
          timeline: [
            { title: '发起解除劳动合同协议', time: '2026-05-18 09:30:00', desc: 'HR王经理发起解除劳动合同协议', color: 'blue' }
          ]
        },
        {
          id: 3,
          name: '王五',
          idCard: '110123199003033456',
          phone: '13800138003',
          customer: '字节跳动',
          workCenter: '上海商务中心',
          contractStartDate: '2026-04-20',
          contractBody: '上海人才服务有限公司',
          contractTemplate: 'negotiate',
          remark: '协商解除劳动合同',
          status: 'expired',
          createTime: '2026-04-20 09:00:00',
          timeline: [
            { title: '合同已过期', time: '2026-05-10 23:59:59', desc: '合同超过有效期未签署', color: 'red' },
            { title: '发起解除劳动合同协议', time: '2026-04-20 09:30:00', desc: 'HR张经理发起解除劳动合同协议', color: 'blue' }
          ]
        },
        {
          id: 4,
          name: '赵六',
          idCard: '330123199004044567',
          phone: '13800138004',
          customer: '美团',
          workCenter: '深圳研发中心',
          contractStartDate: '2026-05-22',
          contractBody: '深圳外包服务有限公司',
          contractTemplate: 'standard',
          remark: '正式员工合同到期不续约',
          status: 'pending',
          createTime: '2026-05-22 09:00:00',
          timeline: []
        },
        {
          id: 5,
          name: '孙七',
          idCard: '510123199005055678',
          phone: '13800138005',
          customer: '腾讯科技',
          workCenter: '北京运营中心',
          contractStartDate: '2026-05-10',
          contractBody: '深圳外包服务有限公司',
          contractTemplate: 'negotiate',
          remark: '员工拒绝签署合同，需要重新沟通',
          status: 'rejected',
          createTime: '2026-05-10 09:00:00',
          timeline: [
            { title: '员工拒绝签署', time: '2026-05-12 15:30:00', desc: '员工明确拒绝签署合同', color: 'red' },
            { title: '发起解除劳动合同协议', time: '2026-05-10 09:30:00', desc: 'HR李经理发起解除劳动合同协议', color: 'blue' }
          ]
        },
        {
          id: 6,
          name: '周八',
          idCard: '420123199006066789',
          phone: '13800138006',
          customer: '阿里巴巴',
          workCenter: '上海商务中心',
          contractStartDate: '2026-05-25',
          contractBody: '北京人力资源服务公司',
          contractTemplate: 'standard',
          remark: '管理层调整需要离职',
          status: 'signing',
          createTime: '2026-05-25 09:00:00',
          timeline: [
            { title: '发起解除劳动合同协议', time: '2026-05-25 09:30:00', desc: 'HR陈经理发起解除劳动合同协议', color: 'blue' }
          ]
        },
        {
          id: 7,
          name: '吴九',
          idCard: '610123199007077890',
          phone: '13800138007',
          customer: '字节跳动',
          workCenter: '深圳研发中心',
          contractStartDate: '2026-05-08',
          contractBody: '上海人才服务有限公司',
          contractTemplate: 'standard',
          remark: '项目结束员工离职',
          status: 'archived',
          createTime: '2026-05-08 09:00:00',
          timeline: [
            { title: '合同自动归档', time: '2026-05-09 16:20:00', desc: '员工签署完成，系统自动归档合同', color: 'green' },
            { title: '员工签署完成', time: '2026-05-09 11:05:00', desc: '吴九通过电子签章完成合同签署', color: 'green' },
            { title: '合同催签', time: '2026-05-08 20:00:00', desc: '系统自动发送催签短信提醒', color: 'orange' },
            { title: '发起解除劳动合同协议', time: '2026-05-08 09:30:00', desc: 'HR刘经理发起解除劳动合同协议', color: 'blue' }
          ]
        },
        {
          id: 8,
          name: '郑十',
          idCard: '370123199008088901',
          phone: '13800138008',
          customer: '美团',
          workCenter: '北京运营中心',
          contractStartDate: '2026-05-28',
          contractBody: '深圳外包服务有限公司',
          contractTemplate: 'standard',
          remark: '家庭原因离职',
          status: 'pending',
          createTime: '2026-05-28 10:00:00',
          timeline: []
        },
        {
          id: 9,
          name: '钱十一',
          idCard: '210123199009099012',
          phone: '13800138009',
          customer: '腾讯科技',
          workCenter: '上海商务中心',
          contractStartDate: '2026-04-15',
          contractBody: '北京人力资源服务公司',
          contractTemplate: 'negotiate',
          remark: '合同到期不续签',
          status: 'signed',
          createTime: '2026-04-15 09:00:00',
          timeline: [
            { title: '合同自动归档', time: '2026-04-18 10:00:00', desc: '员工签署完成，系统自动归档合同', color: 'green' },
            { title: '员工签署完成', time: '2026-04-18 09:30:00', desc: '钱十一通过电子签章完成合同签署', color: 'green' },
            { title: '发起解除劳动合同协议', time: '2026-04-15 09:30:00', desc: 'HR赵经理发起解除劳动合同协议', color: 'blue' }
          ]
        },
        {
          id: 10,
          name: '孙十二',
          idCard: '130123199010101123',
          phone: '13800138010',
          customer: '阿里巴巴',
          workCenter: '深圳研发中心',
          contractStartDate: '2026-05-01',
          contractBody: '上海人才服务有限公司',
          contractTemplate: 'standard',
          remark: '违反公司规章制度被劝退',
          status: 'rejected',
          createTime: '2026-05-01 14:00:00',
          timeline: [
            { title: '员工拒绝签署', time: '2026-05-05 10:20:00', desc: '员工拒绝签署劳动合同协议', color: 'red' },
            { title: '发起解除劳动合同协议', time: '2026-05-01 14:30:00', desc: 'HR周经理发起解除劳动合同协议', color: 'blue' }
          ]
        }
      ]
      resignationDataSource.setData(thirdPartyData)
      thirdPartyData.forEach(item => {
        if (item.status === 'pending') {
          setTimeout(() => {
            resignationDataSource.updateStatus(item.id, 'signing', {
              title: '系统自动发起合同',
              desc: `第三方推送数据，系统自动发起合同签署流程`,
              color: 'blue'
            })
            this.pushToThirdParty()
          }, 500)
        }
      })
    },
    pushToThirdParty() {
      const result = resignationDataSource.pushToThirdParty()
      console.log('推送数据给第三方:', result)
    }
  }
}
</script>

<style scoped>
.resignation-management {
  padding: 24px;
  background: #FFFFFF;
  overflow-x: auto;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;
}

:deep(.ant-select) .ant-select-selector {
  border-color: #D7D7DB !important;
  transition: all 0.3s ease !important;
}

:deep(.ant-select:not(.ant-select-disabled):hover) .ant-select-selector {
  border-color: #FC6732 !important !important;
}

:deep(.ant-select-focused) .ant-select-selector,
:deep(.ant-select-selector:focus),
:deep(.ant-select-selector:active) {
  border-color: #FC6732 !important !important;
  box-shadow: 0 0 0 2px rgba(252, 103, 50, 0.2) !important !important;
}

:deep(.ant-select-open) .ant-select-selector {
  border-color: #FC6732 !important !important;
}

:deep(.ant-select-dropdown-menu-item:hover) {
  background-color: #F7F8FA !important;
  color: #1B1C33 !important;
}

:deep(.ant-select-dropdown-menu-item-selected),
:deep(.ant-select-dropdown-menu-item-selected:hover) {
  background-color: #F7F8FA !important;
  color: #FC6732 !important !important;
}

:deep(.ant-select-dropdown-menu-item-active),
:deep(.ant-select-dropdown-menu-item-active:hover) {
  background-color: #F7F8FA !important;
  color: #FC6732 !important !important;
}

.main-content {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.filter-section {
  display: flex;
  align-items: center;
  column-gap: 16px;
  border: 1px solid #D7D7DB;
  border-radius: 8px;
  background: #F7F8FA;
  padding: 23px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  flex-shrink: 0;
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  flex: 1;
}

.filter-item {
  min-width: 0;
  display: block;
  width: 100%;
}

.filter-item :deep(.ant-select),
.filter-item :deep(.ant-input),
.filter-item :deep(.ant-cascader) {
  width: 100% !important;
  display: block !important;
}

.filter-actions {
  display: inline-flex;
  align-items: flex-start;
  column-gap: 16px;
  box-shadow: inset 1px 0px 0px 0px #D7D7DB;
  background: #F6F7F8;
  padding-left: 16px;
}

.filter-actions :deep(.ant-btn) {
  height: 32px;
  border-radius: 4px;
  border-color: #D7D7DB;
  color: #1B1C33;
}

.filter-actions :deep(.ant-btn:hover) {
  color: #FC6732;
  border-color: #FC6732;
}

.filter-actions :deep(.ant-btn-primary) {
  background-color: #FC6732;
  border-color: #FC6732;
  color: #FFFFFF;
}

.filter-actions :deep(.ant-btn-primary:hover) {
  background-color: #FC7647;
  border-color: #FC7647;
  color: #FFFFFF;
}

.table-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 8px;
  overflow: visible;
  min-height: 0;
  margin-bottom: 24px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 16px 0;
  border-bottom: 1px solid #E8E8E8;
}

.table-header-left {
  display: flex;
  gap: 24px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-label {
  font-size: 13px;
  color: #6B7280;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #1B1C33;
}

.stat-value.primary {
  color: #516EFD;
}

.stat-value.warning {
  color: #FEAE4D;
}

.stat-value.success {
  color: #0DD78D;
}

.stat-value.danger {
  color: #FD5451;
}

.table-header-right {
  display: flex;
}

.table-header-right :deep(.ant-btn) {
  height: 32px;
  border-radius: 4px;
  border-color: #D7D7DB;
  color: #1B1C33;
}

.table-header-right :deep(.ant-btn:hover) {
  color: #FC6732;
  border-color: #FC6732;
}

.table-header-right :deep(.ant-btn-primary) {
  background-color: #FC6732;
  border-color: #FC6732;
  color: #FFFFFF;
}

.table-header-right :deep(.ant-btn-primary:hover) {
  background-color: #FC7647;
  border-color: #FC7647;
  color: #FFFFFF;
}

:deep(.ant-table) {
  background: #fff;
}

:deep(.ant-table-thead > tr > th) {
  background: #F7F8FA;
  color: #1B1C33;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid #D7D7DB;
  padding: 12px 16px;
}

:deep(.ant-table-tbody > tr > td) {
  padding: 12px 16px;
  border-bottom: 1px solid #E8E8E8;
  font-size: 14px;
  color: #1B1C33;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #FFF9F7;
}

:deep(.ant-table-tbody > tr:last-child > td) {
  border-bottom: none;
}

.link-button {
  background: none;
  border: none;
  padding: 0;
  color: #516EFD;
  cursor: pointer;
  font-size: 13px;
  margin-right: 8px;
}

.link-button:hover {
  color: #FC6732;
  text-decoration: underline;
}

.link-button.warning {
  color: #FEAE4D;
}

.link-button.danger {
  color: #FD5451;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0;
}

.pagination-wrapper :deep(.ant-pagination-item-active) {
  border-color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-item-active a) {
  color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-item:hover) {
  border-color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-item:hover a) {
  color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-prev:hover .ant-pagination-item-link),
.pagination-wrapper :deep(.ant-pagination-next:hover .ant-pagination-item-link) {
  border-color: #FC6732;
  color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-options .ant-select-selection-item) {
  color: #FC6732;
}

.ant-modal-footer .ant-btn-primary {
  background: #FC6732 !important;
  border-color: #FC6732 !important;
}

.ant-modal-footer .ant-btn-primary:hover {
  background: #e55a2a !important;
  border-color: #e55a2a !important;
}

.detail-section {
  padding: 8px 0;
}

.detail-title {
  font-size: 15px;
  font-weight: 600;
  color: #1B1C33;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #E8E8E8;
}

.drawer-content {
  width: 100%;
}

.form-wrapper {
  width: 100%;
}

.form-title {
  font-size: 16px;
  font-weight: 600;
  color: #1B1C33;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 24px;
  box-sizing: border-box;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  align-items: start;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 14px;
  color: #8D8E99;
}

.detail-value {
  font-size: 14px;
  color: #1B1C33;
}

.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.timeline-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-left: 2px solid #EDEDF0;
  margin-left: 6px;
  padding-left: 16px;
  position: relative;
}

.timeline-dot {
  position: absolute;
  left: -6px;
  top: 16px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.timeline-content {
  flex: 1;
  padding-bottom: 8px;
}

.timeline-title {
  font-size: 14px;
  font-weight: 600;
  color: #1B1C33;
}

.timeline-time {
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 2px;
}

.timeline-desc {
  font-size: 13px;
  color: #6B7280;
  margin-top: 4px;
}

:deep(.ant-modal .ant-btn) {
  height: 32px;
  border-radius: 4px;
  border-color: #D7D7DB;
  color: #1B1C33;
}

:deep(.ant-modal .ant-btn:hover) {
  color: #FC6732;
  border-color: #FC6732;
}

:deep(.ant-modal .ant-btn-primary) {
  background-color: #FC6732;
  border-color: #FC6732;
  color: #FFFFFF;
}

:deep(.ant-modal .ant-btn-primary:hover) {
  background-color: #FC7647;
  border-color: #FC7647;
  color: #FFFFFF;
}
</style>
